import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { CalculationInput, SoPropertyInfoDto, SoPropertyInsuranceNeedsInput } from '../api';
import { useSessionStorage } from '../hooks';
import { BrandEnum } from '../enums';
import { useGeneralSoContext } from './generalSo.context';
import { AdditionalInfo } from 'src/pages/app/offerPreview.page';
import dayjs from 'dayjs';
import { INSURANCE_NEEDS_KEY } from '../constants';

interface CalculationContextType {
  setAdditionalCoverages: (brandCode: string, coverageCode: string) => void;
  handleInsuranceNeeds: (insuranceNeeds: string[], addNeeds?: boolean) => void;
  setFilterData: (filterData: AdditionalInfo[]) => void;
  setBrandCode: (brandCode: BrandEnum | undefined) => void;
  setPackageCode: (packageCode: string | undefined) => void;
  updatePropertyInfo: (propertyInfo: Partial<CalculationInput['propertyInfo']>) => void;
  calculationInput: CalculationInput | null;
  additionalCoverages: { brandCode: string, coverageCode: string }[];
}

const CalculationContext = createContext<CalculationContextType | undefined>(undefined);

export const useCalculationInput = (): CalculationContextType => {
  const context = useContext(CalculationContext);
  if (context === undefined) {
    throw new Error('useCalculationInput must be used within a CalculationProvider');
  }
  return context;
};

interface CalculationRequestProps {
  children: ReactNode;
}

export const CalculationProvider: React.FC<CalculationRequestProps> = ({ children }) => {
  const { soInitialData } = useGeneralSoContext();
  const { getValue: getCalculationInput, setValue: setSessionCalc } = useSessionStorage<CalculationInput | null>('calculationInput');
  const { getValue: getInsuranceNeeds } = useSessionStorage<SoPropertyInsuranceNeedsInput>(INSURANCE_NEEDS_KEY);
  const [calculationInput, setCalculationInput] = useState<CalculationInput | null>(null);
  const [additionalCoverages, setAdditionalCoverages] = useState<{ brandCode: string, coverageCode: string}[]>([]);

  useEffect(() => {
    getCalcInput();
  }, [soInitialData]);

  const convertInsuranceNeeds = (needs?: string[]) => {
    const sessionNeeds = needs || getInsuranceNeeds()?.risks;
    const selectedNeeds = sessionNeeds?.map((risk) => ({ brandCode: BrandEnum.WIENER, coverageCode: risk })) || [];
    setAdditionalCoverages(selectedNeeds);
    return selectedNeeds;
  }

  const getCalcInput = () => {
    if (!sessionStorage.getItem('sessionId')) {
      sessionStorage.setItem('sessionId', self.crypto.randomUUID());
    }

    const sessionId = sessionStorage.getItem('sessionId') || undefined;
    const sessionCalcInput = getCalculationInput();
    if (soInitialData?.propertyInfo && !sessionCalcInput) {
      const propertyInfo = soInitialData.propertyInfo as SoPropertyInfoDto;
      const selectedCoverages = convertInsuranceNeeds();
      const newCalcInput = {
        sessionId,
        propertyInfo: {
          type: propertyInfo.type!,
          cityKompareId: propertyInfo.cityKompareId!,
          streetKompareId: propertyInfo.streetKompareId!,
          cityParentName: propertyInfo.cityParentName!,
          zipCode: propertyInfo.zipCode!,
          city: propertyInfo.city!,
          street: propertyInfo.street!,
          houseNumber: propertyInfo.houseNumber!,
          area: propertyInfo.area!,
          floor: propertyInfo.floor!,
          usage: propertyInfo.usage!,
          masonry: propertyInfo.masonry!,
          constructionYear: {
            exactYear: propertyInfo.constructionYear?.exactYear || 1965,
            before1964: propertyInfo.constructionYear?.before1964 || false,
          },
        },
        insuranceQuotes: {
          building: 1200,
          household: 500,
        },
        buildingInsurance: true,
        householdInsurance: true,
        isFlood: false,
        policyDuration: 99,
        policyStartDate: dayjs().add(2, 'day').toISOString(),
        paymentMethod: 'single',
        selectedCoverages,
        webshopCalc: [BrandEnum.WIENER, BrandEnum.GRAWE]
      }
      setCalculationInput(newCalcInput);
      setSessionCalc(newCalcInput);
    } else if(sessionCalcInput) {
      const propertyInfo = soInitialData?.propertyInfo as SoPropertyInfoDto | undefined;
      const newCalcInput = {
        ...sessionCalcInput,
        webshopCalc: [BrandEnum.WIENER, BrandEnum.GRAWE],
        propertyInfo: propertyInfo ? {
          ...sessionCalcInput.propertyInfo,
          area: propertyInfo.area ?? sessionCalcInput.propertyInfo.area,
          constructionYear: {
            exactYear: propertyInfo.constructionYear?.exactYear ?? sessionCalcInput.propertyInfo.constructionYear?.exactYear,
            before1964: propertyInfo.constructionYear?.before1964 ?? sessionCalcInput.propertyInfo.constructionYear?.before1964,
          },
        } : sessionCalcInput.propertyInfo,
      };
      setCalculationInput(newCalcInput);
      setSessionCalc(newCalcInput);
      setAdditionalCoverages(sessionCalcInput.selectedCoverages || [])
    }
  }

  const handleCalculationInput = (newCalculationInput: CalculationInput) => {
    setCalculationInput(newCalculationInput);
    setSessionCalc(newCalculationInput);
  }

  const updatePropertyInfo = (partial: Partial<CalculationInput['propertyInfo']>) => {
    const base = getCalculationInput() ?? calculationInput;
    if (!base) return; // no calc input yet; getCalcInput builds it fresh from soInitialData
    handleCalculationInput({
      ...base,
      propertyInfo: {
        ...base.propertyInfo,
        ...partial,
      },
    });
  };

  const handleBrandCode = (newBrandCode: BrandEnum | undefined) => {
    const sessionCalcInput = getCalculationInput();
    handleCalculationInput({
      ...sessionCalcInput,
      brandCode: newBrandCode
    } as CalculationInput);
  }

  const handlePackageCode = (newPackageCode: string | undefined) => {
    const sessionCalcInput = getCalculationInput();
    handleCalculationInput({
      ...sessionCalcInput,
      packageCode: newPackageCode
    } as CalculationInput);
  }

  const getNewCoverages = (coverages: { brandCode: string, coverageCode: string }[], addNeeds: boolean = true) => {
    if (!calculationInput?.selectedCoverages) return [];

    const foundCoverage = calculationInput.selectedCoverages.find(sC =>
      coverages.some(c => c.coverageCode === sC.coverageCode));
    if (foundCoverage && !addNeeds) {
      return calculationInput.selectedCoverages.filter(sC =>
        !coverages.some(c => c.coverageCode === sC.coverageCode));
    }

    if (addNeeds) {
      const hasFranchise = calculationInput.selectedCoverages.find(sC => sC.coverageCode === 'earthquakeFranchise');
      const incoming = hasFranchise
        ? [...coverages.filter(c => c.coverageCode !== 'earthquake'), hasFranchise]
        : coverages;
      return incoming;
    }


    const conflicts: Record<string, string> = {
      earthquake: 'earthquakeFranchise',
      earthquakeFranchise: 'earthquake',
    };
    const codesToReplace = coverages
      .map(c => conflicts[c.coverageCode])
      .filter(Boolean);

    return [
      ...calculationInput.selectedCoverages.filter(sC => !codesToReplace.includes(sC.coverageCode)),
      ...coverages,
    ];
  }

  const handleAdditionalCoverages = (coveragesBrandCode: string, coverageCode: string) => {
    if (calculationInput?.selectedCoverages) {
      const newAdditionalCoverages = getNewCoverages([{ brandCode: coveragesBrandCode, coverageCode}], false);
      setAdditionalCoverages(newAdditionalCoverages);
      handleCalculationInput({
        ...calculationInput,
        selectedCoverages: newAdditionalCoverages
      } as CalculationInput);
    }
  }

  const handleInsuranceNeeds = (needs: string[], addNeeds?: boolean) => {
    if (!calculationInput?.selectedCoverages) return;

    const mappedNeeds = convertInsuranceNeeds(needs);
    const newAdditionalCoverages = getNewCoverages(mappedNeeds, addNeeds);

    setAdditionalCoverages(newAdditionalCoverages);
    handleCalculationInput({
      ...calculationInput,
      webshopCalc: [BrandEnum.WIENER, BrandEnum.GRAWE],
      selectedCoverages: newAdditionalCoverages,
    } as CalculationInput);
  }

  const setFilterData = (filterData: AdditionalInfo[]) => {
    if (calculationInput) {
      handleCalculationInput({
        ...calculationInput,
        propertyInfo: {
          ...calculationInput.propertyInfo,
          area: filterData.find(aI => aI.key === "area")?.value || calculationInput.propertyInfo.area,
          floor: filterData.find(aI => aI.key === "floor")?.value || calculationInput.propertyInfo.floor,
          usage: filterData.find(aI => aI.key === "usage")?.value || calculationInput.propertyInfo.usage,
          masonry: filterData.find(aI => aI.key === "masonry")?.value || calculationInput.propertyInfo.masonry,
        },
        isFlood: filterData.find(aI => aI.key === "isFlood")?.value,
        policyDuration: filterData.find(aI => aI.key === "policyDuration")?.value || calculationInput.policyDuration,
        policyStartDate: filterData.find(aI => aI.key === "policyStartDate")?.value || calculationInput.policyStartDate,
        insuranceQuotes: {
          building: filterData.find(aI => aI.key === "buildingQuote")?.value || calculationInput.insuranceQuotes?.building,
          household: filterData.find(aI => aI.key === "householdQuote")?.value || calculationInput.insuranceQuotes?.household
        }
      });
    }
  }

  const value: CalculationContextType = {
    handleInsuranceNeeds,
    setAdditionalCoverages: handleAdditionalCoverages,
    setFilterData,
    setBrandCode: handleBrandCode,
    setPackageCode: handlePackageCode,
    updatePropertyInfo,
    calculationInput,
    additionalCoverages
  };

  return (
    <CalculationContext.Provider value={value}>
      {children}
    </CalculationContext.Provider>
  );
};
